import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import { Link, useLocation } from 'react-router-dom'
import { HiOutlineHome, HiOutlineLogout } from 'react-icons/hi'
import { DASHBOARD_SIDEBAR_LINKS, DASHBOARD_SIDEBAR_BOTTOM_LINKS } from '../../lib/constants'
import { getSidebarJourneyMembership, isWorkspaceLinkActive } from '../../lib/navigation/workspaceNavigation'
import logo from '../../assets/logo_rigo_v2.png'
import { useAccess } from '../../access/AccessContext'
import { canSeeNavigationItem } from '../../access/navigationAccess'

const linkClass =
	'group relative flex min-h-10 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium leading-5 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-1'

const homeLink = {
	key: 'home',
	label: 'Home',
	path: '/',
	icon: <HiOutlineHome aria-hidden="true" />,
}

function SidebarTooltip({ label, show }) {
	if (!show) return null

	return (
		<div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 z-50 pointer-events-none">
			<div className="bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-md shadow-lg whitespace-nowrap">
				{label}
			</div>
			<div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45" />
		</div>
	)
}

export default function Sidebar({ onLogout, sidebarOpen }) {
	const access = useAccess()
	const groupedLinks = useMemo(() => {
		return DASHBOARD_SIDEBAR_LINKS.filter((link) => canSeeNavigationItem(access, link.key)).reduce((acc, link) => {
			const section = link.section || 'General'
			if (!acc[section]) acc[section] = []
			acc[section].push(link)
			return acc
		}, {})
	}, [access])

	return (
		<div
			className={classNames(
				'bg-slate-50 backdrop-blur-sm p-3 h-full min-h-0 flex flex-col transition-all duration-300 shrink-0 border-r border-stone-300',
				sidebarOpen ? 'w-84' : 'w-16'
			)}
		>
			<div className="mb-4 flex flex-col items-center border-b border-stone-300 pb-3">

				<img
					src={logo}
					alt="Logo"
					className={classNames(
						'object-contain transition-all duration-300',
						sidebarOpen ? 'w-10' : 'w-8'
					)}
				/>

				{sidebarOpen && (
					<>
						<p className="mt-1.5 text-[10px] uppercase tracking-[0.18em] text-gray-500">
							R I G O
						</p>

					</>
				)}
			</div>

			<nav
				aria-label="Workspace navigation"
				data-testid="sidebar-navigation"
				className="flex flex-1 min-h-0 flex-col overflow-x-hidden overflow-y-auto pr-1"
			>
				{Object.entries(groupedLinks).map(([section, links], index) => (
					<section
						key={section}
						data-sidebar-section={section}
						aria-label={!sidebarOpen ? section : undefined}
						aria-labelledby={sidebarOpen ? `sidebar-section-${index}` : undefined}
						className={classNames(sidebarOpen ? 'pb-5' : 'pb-1')}
					>
						{index > 0 && (
							<div
								aria-hidden="true"
								data-sidebar-divider
								className={classNames(
									'border-t border-slate-300/80',
									sidebarOpen ? 'mb-4 mt-1' : 'mb-1.5 mt-0.5'
								)}
							/>
						)}
						{sidebarOpen && (
							<p
								id={`sidebar-section-${index}`}
								data-sidebar-heading
								className="mb-2.5 whitespace-nowrap px-3 text-[10px] font-bold uppercase leading-4 tracking-[0.12em] text-slate-700"
							>
								{section}
							</p>
						)}

						<div className="flex flex-col gap-1">
							{links.map((link, linkIndex) => {
								const journey = getSidebarJourneyMembership(link)
								const previousJourney = getSidebarJourneyMembership(links[linkIndex - 1] || {})
								const nextJourney = getSidebarJourneyMembership(links[linkIndex + 1] || {})

								return (
								<SidebarLink
									key={link.key}
									link={link}
									sidebarOpen={sidebarOpen}
									journey={journey}
									journeyStart={journey !== previousJourney}
									journeyEnd={journey !== nextJourney}
								/>
								)
							})}
						</div>
					</section>
				))}
			</nav>

			<div data-testid="sidebar-footer" className="mt-4 pt-3 border-t border-stone-300 flex flex-col gap-1">
				{sidebarOpen && DASHBOARD_SIDEBAR_BOTTOM_LINKS.length > 0 && (
					<p className="text-[11px] uppercase text-gray-400 px-3 mb-2 tracking-[0.18em]">
						Workspace
					</p>
				)}

				{DASHBOARD_SIDEBAR_BOTTOM_LINKS.map((link) => (
					<SidebarLink
						key={link.key}
						link={link}
						sidebarOpen={sidebarOpen}
					/>
				))}

				<SidebarLink link={homeLink} sidebarOpen={sidebarOpen} />

				<LogoutButton onLogout={onLogout} sidebarOpen={sidebarOpen} />

				{sidebarOpen && (
					<>
						<p className="text-[10px] text-center tracking-[0.18em] text-gray-400 mt-2">
							Powered by PHD Consulting
						</p>

					</>
				)}
			</div>
		</div>
	)
}

function SidebarLink({ link, sidebarOpen, journey = null, journeyStart = false, journeyEnd = false }) {
	const { pathname } = useLocation()
	const [hovered, setHovered] = useState(false)

	const isActive = isWorkspaceLinkActive(link, pathname)

	return (
		<div
			className="relative"
			data-journey={sidebarOpen && journey ? journey : undefined}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			{sidebarOpen && journey && (
				<span
					aria-hidden="true"
					data-journey-rail
					className={classNames(
						'pointer-events-none absolute left-1.5 z-10 w-px',
						journey === 'analytical' ? 'bg-emerald-600/25' : 'bg-blue-600/25',
						journeyStart ? 'top-1/2' : '-top-0.5',
						journeyEnd ? 'bottom-1/2' : '-bottom-0.5'
					)}
				/>
			)}
			<Link
				to={link.path}
				aria-label={link.label}
				aria-current={isActive ? 'page' : undefined}
				className={classNames(
					linkClass,
					isActive
						? 'bg-sky-100/80 text-slate-900 shadow-[inset_3px_0_0_0_rgb(3_105_161)]'
						: 'text-slate-600 hover:bg-slate-200/80 hover:text-slate-900',
					!sidebarOpen && 'justify-center'
				)}
			>
				<span
					className={classNames(
						'text-[19px] shrink-0 transition-colors',
						isActive ? 'text-sky-700' : 'text-slate-500 group-hover:text-slate-700'
					)}
				>
					{link.icon}
				</span>

				{sidebarOpen && (
					<div className="flex flex-col min-w-0">
						<span className="truncate">{link.label}</span>
					</div>
				)}
			</Link>

			<SidebarTooltip label={link.label} show={!sidebarOpen && hovered} />
		</div>
	)
}

function LogoutButton({ onLogout, sidebarOpen }) {
	const [hovered, setHovered] = useState(false)

	return (
		<div
			className="relative"
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			<button
				onClick={onLogout}
				className={classNames(
					linkClass,
					'text-gray-600 hover:bg-red-100 hover:text-red-600 w-full text-left',
					!sidebarOpen && 'justify-center'
				)}
			>
				<span className="text-[20px] shrink-0">
					<HiOutlineLogout />
				</span>

				{sidebarOpen && <span>Sign out</span>}
			</button>

			<SidebarTooltip label="Sign out" show={!sidebarOpen && hovered} />
		</div>
	)
}
